import React, { Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import useModal from '@/hooks/useModal';
import { Button, RadioButton, Typography } from '../common';
import { Textarea } from '../form';
import { useNotification } from '@/hooks';
import Image from 'next/image';
import { X } from 'lucide-react';
import { feedbackService } from '@/api/feedback';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import AppRoutes from '@/const/routes';
import { useRouter } from 'next/router';

interface Props {
	user: any;
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const SurveyModal: React.FC<Props> = ({ user, isOpen, setIsOpen }) => {
	const router = useRouter();
	const { toastError, toastSuccess } = useNotification();
	const [scale, setScale] = React.useState<string>();
	const [rate, setRate] = React.useState<string>();
	const [message, setMessage] = React.useState<string>('');

	const { register } = useForm();
	const options = Array.from({ length: 11 }, (_, index) => ({
		id: `${index}`,
		value: `${index}`,
		label: `${index}`,
	}));

	const experienceRate = [
		{
			id: '1',
			value: '5',
			label: 'Very satisfied',
		},
		{
			id: '2',
			value: '4',
			label: 'Satisfied',
		},
		{
			id: '3',
			value: '3',
			label: 'Neutral',
		},
		{
			id: '4',
			value: '2',
			label: 'Dissatisfied',
		},
		{
			id: '5',
			value: '1',
			label: 'Very dissatisfied',
		},
	];

	const { Modal, show, hide } = useModal();

	React.useEffect(() => {
		if (!user) return;
		const showSurvey = getLocalStorage('showSurvey');
		const showUserActiveModal = router.query['showUserActiveModal'];
		const showCompleteAccount =
			user.user.role === 'client' &&
			(!user.user.profile_completion_date || !!router.query['confirm-account']);

		if (showSurvey === false || showCompleteAccount || showUserActiveModal)
			return;
		// Deactivated for David's request
		// if (user?.user?.feedbackFlag) {
		// 	show();
		// }
	}, [user]);

	React.useEffect(() => {
		if (!isOpen) {
			setScale(undefined);
			setRate(undefined);
			setMessage('');
		} else {
			show();
		}
	}, [isOpen]);

	const handleSubmit = async () => {
		try {
			const request = {
				probabilityOfRecommendation: Number(scale),
				rate: Number(rate),
				reason: message,
			};
			await feedbackService.post(request);
			toastSuccess(
				'Feedback successfully submitted. Thank you for sharing your thoughts!'
			);
			await router.push(AppRoutes.CLIENTAPP_DASHBOARD);
			handleClose();
		} catch (error) {
			toastError(
				'An error occurred while submitting your feedback. Please try again.'
			);
		}
	};

	const handleClose = () => {
		setIsOpen(false);
		setLocalStorage('showSurvey', false);
		hide();
	};

	const isDisabled = !scale || !rate;

	return (
		<Modal
			avoidClose
			title={
				<div className="flex items-center justify-center gap-2 relative">
					<div className="bg-purple-150 p-2 rounded-xl hidden md:block">
						<Image src={'/img/common/hat.png'} alt="" width={23} height={23} />
					</div>
					<Typography type="bold-36">
						We&apos;d love to{' '}
						<span className="text-purple-150">hear from you!</span>
					</Typography>
					<button className="absolute -right-5 -top-6" onClick={handleClose}>
						<X color="#292D32" />
					</button>
				</div>
			}
			noMaxHeight
			classNameTitle="!mb-0 lg:!mb-6"
			classNameCardContainer={'w-full xl:w-[950px] !px-4 lg:!p-12'}
			classNameContainerChildren="flex flex-col overflow-y-auto max-h-[60vh] lg:max-h-[80vh] py-4"
		>
			<div className="space-y-12 mt-6 h-auto max-h-full overflow-y-auto scroll-custom pr-4">
				<div className="space-y-4">
					<Typography type="regular-16" className="text-left">
						1. On a scale of 0 to 10, how likely are you to recommend Claim
						Magic to a friend or colleague?
					</Typography>
					<div className="flex justify-center items-center w-full">
						<Typography type="regular-16" className="mt-4 hidden md:block">
							Not at all likely
						</Typography>
						<RadioButton
							size="sm"
							options={options}
							className={'flex items-baseline gap-2 !ml-4 md:gap-6 mx-3'}
							classNameContainer={'flex-col items-center space-x-0 space-y-3'}
							onChange={setScale}
						/>
						<Typography type="regular-16" className="mt-5 hidden md:block">
							Extremely likely
						</Typography>
					</div>
				</div>
				<div className="space-y-4">
					<Typography type="regular-16" className="text-left">
						2. How would you rate your overall experience with Claim Magic?
					</Typography>
					<RadioButton
						size="sm"
						options={experienceRate}
						orientation="vertical"
						classNameContainer="md:!mb-5 ml-4"
						onChange={setRate}
					/>
				</div>
				<div className="space-y-4">
					<Typography type="regular-16" className="text-left">
						3. What is the main reason for your satisfaction or dissatisfaction
						with Claim Magic?
					</Typography>
					<Textarea
						rows={3}
						name="message"
						register={register}
						className="mt-0"
						placeholder="Write your answer here"
						onChange={({ target }) => setMessage(target.value)}
					/>
				</div>
			</div>
			<Button
				disabled={isDisabled}
				decoration="primary"
				size="medium"
				className="!w-64 !h-16 m-auto mt-12"
				onClick={handleSubmit}
			>
				Submit
			</Button>
		</Modal>
	);
};
